import type { QuestionWithSaved } from "~vue/api/generated/reflections";
import { i18n } from "~vue/i18n";

/**
 * Returns true when the question has a meaningful response:
 * - Score question: score selected OR "not observed" checked.
 * - Text question: non-empty text.
 */
export function isQuestionAnswered(q: QuestionWithSaved): boolean {
    if (q.type === "Score") {
        return q.saved.score != null || q.saved.not_observed === true;
    }
    return (q.saved.text ?? "").trim().length > 0;
}

/**
 * Returns true when a question is required but has not been answered.
 */
export function isRequiredUnanswered(q: QuestionWithSaved): boolean {
    return !q.is_optional && !isQuestionAnswered(q);
}

// Question text templates are stored in the DB with a `{{ name }}` placeholder
// so the same rows can power both 360s (subject varies) and self reflections
// (subject is the viewer). Callers pass the correct name for their context.
const NAME_PLACEHOLDER_RE = /\{\{\s*name\s*\}\}/g;

export function resolveQuestionName(text: string, subjectFirstName: string): string {
    return text.replace(NAME_PLACEHOLDER_RE, subjectFirstName);
}

export function ordinalWord(n: number): string {
    return i18n.global.t("reflections.shared.ordinal", { n });
}

// Literal switches, not key-lookup templates, so the vue-i18n ESLint catalogue check can see
// every assessments.scales.<code>.points.<n> / .notObserved key is used (BRA-1259). Mirrors the
// convention in pages/PastConversations.vue's translateBucketLabel and
// composables/useRealtimeSession.ts.
function translateScalePoint(scale: string, pointIndex: number): string | null {
    const { t } = i18n.global;
    switch (scale) {
        case "rarely_consistently":
            switch (pointIndex) {
                case 1:
                    return t("assessments.scales.rarely_consistently.points.1");
                case 2:
                    return t("assessments.scales.rarely_consistently.points.2");
                case 3:
                    return t("assessments.scales.rarely_consistently.points.3");
                case 4:
                    return t("assessments.scales.rarely_consistently.points.4");
                case 5:
                    return t("assessments.scales.rarely_consistently.points.5");
                default:
                    return null;
            }
        case "proficiency":
            switch (pointIndex) {
                case 1:
                    return t("assessments.scales.proficiency.points.1");
                case 2:
                    return t("assessments.scales.proficiency.points.2");
                case 3:
                    return t("assessments.scales.proficiency.points.3");
                case 4:
                    return t("assessments.scales.proficiency.points.4");
                case 5:
                    return t("assessments.scales.proficiency.points.5");
                default:
                    return null;
            }
        case "agree_disagree_7":
            switch (pointIndex) {
                case 1:
                    return t("assessments.scales.agree_disagree_7.points.1");
                case 2:
                    return t("assessments.scales.agree_disagree_7.points.2");
                case 3:
                    return t("assessments.scales.agree_disagree_7.points.3");
                case 4:
                    return t("assessments.scales.agree_disagree_7.points.4");
                case 5:
                    return t("assessments.scales.agree_disagree_7.points.5");
                case 6:
                    return t("assessments.scales.agree_disagree_7.points.6");
                case 7:
                    return t("assessments.scales.agree_disagree_7.points.7");
                default:
                    return null;
            }
        case "agree_disagree_5":
            switch (pointIndex) {
                case 1:
                    return t("assessments.scales.agree_disagree_5.points.1");
                case 2:
                    return t("assessments.scales.agree_disagree_5.points.2");
                case 3:
                    return t("assessments.scales.agree_disagree_5.points.3");
                case 4:
                    return t("assessments.scales.agree_disagree_5.points.4");
                case 5:
                    return t("assessments.scales.agree_disagree_5.points.5");
                default:
                    return null;
            }
        case "effectiveness_4":
            switch (pointIndex) {
                case 1:
                    return t("assessments.scales.effectiveness_4.points.1");
                case 2:
                    return t("assessments.scales.effectiveness_4.points.2");
                case 3:
                    return t("assessments.scales.effectiveness_4.points.3");
                case 4:
                    return t("assessments.scales.effectiveness_4.points.4");
                default:
                    return null;
            }
        case "frequency_3":
            switch (pointIndex) {
                case 1:
                    return t("assessments.scales.frequency_3.points.1");
                case 2:
                    return t("assessments.scales.frequency_3.points.2");
                case 3:
                    return t("assessments.scales.frequency_3.points.3");
                default:
                    return null;
            }
        case "needs_improvement_strength":
            switch (pointIndex) {
                case 1:
                    return t("assessments.scales.needs_improvement_strength.points.1");
                case 2:
                    return t("assessments.scales.needs_improvement_strength.points.2");
                case 3:
                    return t("assessments.scales.needs_improvement_strength.points.3");
                case 4:
                    return t("assessments.scales.needs_improvement_strength.points.4");
                case 5:
                    return t("assessments.scales.needs_improvement_strength.points.5");
                default:
                    return null;
            }
        default:
            return null;
    }
}

function translateScaleNotObserved(scale: string): string | null {
    const { t } = i18n.global;
    switch (scale) {
        case "agree_disagree_5":
            return t("assessments.scales.agree_disagree_5.notObserved");
        case "effectiveness_4":
            return t("assessments.scales.effectiveness_4.notObserved");
        case "frequency_3":
            return t("assessments.scales.frequency_3.notObserved");
        case "needs_improvement_strength":
            return t("assessments.scales.needs_improvement_strength.notObserved");
        default:
            return null;
    }
}

/**
 * Translates one scale point's label (BRA-1259).
 *
 * `pointIndex` is the point's 1-based position in the scale's full, un-hidden
 * point set — `ScaleLabels.point_indices` / `PerQuestion.scale_point_indices`
 * carry this, so a label whose neutral midpoint was hidden still keys to its
 * original point number. An empty `rawLabel` (an intentionally blank anchor,
 * e.g. the middle points of `rarely_consistently`) stays empty. A scale code
 * with no translated key — unknown, legacy, or missing from the payload —
 * falls back to the server's own string so the UI still renders something.
 */
export function resolveScalePointLabel(scale: string | null | undefined, rawLabel: string, pointIndex: number): string {
    if (!rawLabel || !scale) {
        return rawLabel;
    }
    return translateScalePoint(scale, pointIndex) ?? rawLabel;
}

/**
 * Translates every label in a scale's point set, given the point index each
 * label actually occupies in the scale's full, un-hidden point set.
 *
 * `pointIndices` is absent on results persisted before BRA-1259. Guessing a
 * point from array position in that case is unsafe: a hidden-neutral report's
 * saved labels (e.g. agree_disagree_5 down to `["Strongly disagree",
 * "Disagree", "Agree", "Strongly agree"]`) would mis-key position 2 to
 * "Neutral" and position 3 to "Agree", silently relabeling an already-published
 * result even in English. When `pointIndices` is missing, or its length
 * doesn't match `labels`, this returns `labels` unchanged instead of guessing.
 */
export function resolveScalePointLabels(scale: string | null | undefined, labels: string[], pointIndices: number[] | null | undefined): string[] {
    if (!scale || !pointIndices || pointIndices.length !== labels.length) {
        return labels;
    }
    return labels.map((label, i) => resolveScalePointLabel(scale, label, pointIndices[i]));
}

/**
 * Translates a scale's "not observed" opt-out caption override (BRA-1259).
 * Falls back to the server's string for a scale with no translated override.
 */
export function resolveScaleNotObservedLabel(scale: string | null | undefined, rawLabel: string): string {
    if (!scale) {
        return rawLabel;
    }
    return translateScaleNotObserved(scale) ?? rawLabel;
}

/**
 * Returns the list of required questions that have not been answered.
 */
export function getUnansweredRequired(questions: QuestionWithSaved[]): QuestionWithSaved[] {
    return questions.filter(isRequiredUnanswered);
}
